import React, { useState } from 'react';
import ResourceModal from './ResourceModal';
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { FaGamepad, FaCheck, FaTimes, FaRedo } from 'react-icons/fa';

interface MisinformationQuizProps {
  onClose: () => void;
}

const MisinformationQuiz: React.FC<MisinformationQuizProps> = ({ onClose }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answeredCorrectly, setAnsweredCorrectly] = useState<boolean | null>(null);

  const questions = [
    {
      question: "A breaking news story claims a politician made a controversial statement. The source is an anonymous Twitter account with a username similar to a major news outlet. What should you do?",
      options: [
        "Share it immediately to inform others",
        "Check if verified news outlets have reported the same statement",
        "Assume it's true if it aligns with the politician's past behavior",
        "Comment your reaction without fact-checking"
      ],
      correctAnswer: 1,
      explanation: "Always verify breaking news with established, credible news sources before sharing. Anonymous accounts that mimic legitimate news outlets are common sources of misinformation."
    },
    {
      question: "You see a dramatic video showing damage from a recent natural disaster. What's a sign that might indicate the video is misleading?",
      options: [
        "The video has high production quality",
        "It was shared by someone living in the affected area",
        "Weather conditions or landscape features don't match the purported location",
        "The footage shows emergency responders"
      ],
      correctAnswer: 2,
      explanation: "Inconsistencies in location details (like weather, vegetation, architecture, or landscape) often indicate that footage has been taken from a different event or location."
    },
    {
      question: "Which type of headline is most likely to contain misinformation?",
      options: [
        "\"Study Suggests Possible Link Between X and Y\"",
        "\"X Might Be Associated with Y, Researchers Say\"",
        "\"X Definitely Causes Y, Shocking Study Reveals\"",
        "\"Researchers Examine Relationship Between X and Y\""
      ],
      correctAnswer: 2,
      explanation: "Absolute claims (using words like 'definitely,' 'proves,' or 'shocking') often oversimplify complex findings. Legitimate scientific reporting typically uses more measured language that acknowledges limitations."
    },
    {
      question: "A website publishes a health article claiming a common food cures a serious disease. Which of these is the BEST reason to be skeptical?",
      options: [
        "The article doesn't cite peer-reviewed studies",
        "The food mentioned is exotic or unusual",
        "You haven't heard this claim before",
        "The website has advertisements"
      ],
      correctAnswer: 0,
      explanation: "Medical claims should be backed by peer-reviewed research published in reputable scientific journals. Articles without proper citations to scientific studies should be treated with skepticism."
    },
    {
      question: "Which image characteristic might suggest AI manipulation or a 'deepfake'?",
      options: [
        "The image shows a public figure in an official setting",
        "The image has inconsistent lighting or unnatural blurring around facial features",
        "The image quality is very high",
        "The image shows multiple people"
      ],
      correctAnswer: 1,
      explanation: "AI-generated images often have telltale flaws, particularly around complex features like eyes, teeth, hands, and where different elements meet (like hair against background). Inconsistent lighting and unnatural blurring are common indicators."
    }
  ];

  const handleAnswerSelected = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    const isCorrect = answerIndex === questions[currentQuestion].correctAnswer;
    setAnsweredCorrectly(isCorrect);
    if (isCorrect) {
      setScore(score + 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setAnsweredCorrectly(null);
    } else {
      setShowResult(true);
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setScore(0);
    setShowResult(false);
    setSelectedAnswer(null);
    setAnsweredCorrectly(null);
  };

  return (
    <ResourceModal title="Misinformation Quiz Challenge" onClose={onClose}>
      <div className="space-y-6">
        <div className="flex items-center gap-4 bg-orange-500/10 rounded-xl p-4">
          <div className="bg-orange-500/20 p-3 rounded-full">
            <FaGamepad className="text-orange-400 h-8 w-8" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Interactive Challenge</h3>
            <p className="text-gray-400">Test your ability to identify various types of misleading content</p>
          </div>
        </div>

        {!showResult ? (
          <div className="space-y-6">
            <div className="bg-black/30 border border-gray-800 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-400">Question {currentQuestion + 1} of {questions.length}</span>
                <span className="text-sm text-gray-400">Score: {score}/{questions.length}</span>
              </div>
              <h3 className="text-lg font-medium mb-4">{questions[currentQuestion].question}</h3>
              
              <div className="space-y-3">
                {questions[currentQuestion].options.map((option, index) => (
                  <button
                    key={index}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedAnswer === index 
                        ? (answeredCorrectly 
                          ? 'bg-green-500/20 border-green-500/40 text-green-400' 
                          : 'bg-red-500/20 border-red-500/40 text-red-400')
                        : 'bg-black/20 border-gray-800 hover:border-truth/40 text-gray-300'
                    } ${selectedAnswer !== null && index === questions[currentQuestion].correctAnswer ? 'bg-green-500/20 border-green-500/40 text-green-400' : ''}`}
                    onClick={() => selectedAnswer === null && handleAnswerSelected(index)}
                    disabled={selectedAnswer !== null}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option}</span>
                      {selectedAnswer === index && (
                        answeredCorrectly 
                          ? <FaCheck className="text-green-400 h-5 w-5" /> 
                          : <FaTimes className="text-red-400 h-5 w-5" />
                      )}
                      {selectedAnswer !== null && index === questions[currentQuestion].correctAnswer && selectedAnswer !== index && (
                        <FaCheck className="text-green-400 h-5 w-5" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedAnswer !== null && (
              <div className={`p-4 rounded-lg ${answeredCorrectly ? 'bg-green-900/20 border border-green-900/40' : 'bg-red-900/20 border border-red-900/40'}`}>
                <h4 className={`font-medium mb-2 ${answeredCorrectly ? 'text-green-400' : 'text-red-400'}`}>
                  {answeredCorrectly ? 'Correct!' : 'Incorrect'}
                </h4>
                <p className="text-gray-300 text-sm mb-4">{questions[currentQuestion].explanation}</p>
                <Button 
                  className="bg-truth hover:bg-truth-dark"
                  onClick={handleNextQuestion}
                >
                  {currentQuestion < questions.length - 1 ? 'Next Question' : 'See Results'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-black/30 border border-gray-800 rounded-xl p-6 text-center">
              <h3 className="text-xl font-bold mb-2">Quiz Complete!</h3>
              <p className="text-gray-400 mb-4">You scored:</p>
              <div className="text-4xl font-bold text-truth mb-4">{score} / {questions.length}</div>
              
              {score === questions.length ? (
                <p className="text-green-400">Excellent! You're a misinformation-spotting expert!</p>
              ) : score >= questions.length * 0.7 ? (
                <p className="text-green-400">Good job! You have solid fact-checking skills.</p>
              ) : score >= questions.length * 0.5 ? (
                <p className="text-yellow-400">Not bad, but there's room for improvement in your fact-checking abilities.</p>
              ) : (
                <p className="text-red-400">You might need more practice with identifying misinformation.</p>
              )}
            </div>
            
            <Button 
              className="bg-truth hover:bg-truth-dark w-full"
              onClick={restartQuiz}
            >
              <FaRedo className="mr-2 h-4 w-4" /> Try Again
            </Button>
            
            <Separator />
            
            <div>
              <h3 className="text-lg font-bold mb-4">Key Takeaways</h3>
              <ul className="space-y-3">
                <li className="flex gap-3 bg-black/20 border border-gray-800 rounded-lg p-4">
                  <div className="bg-truth/20 text-truth h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <p className="text-gray-300 text-sm">Always verify information with multiple trusted sources before believing or sharing it.</p>
                </li>
                <li className="flex gap-3 bg-black/20 border border-gray-800 rounded-lg p-4">
                  <div className="bg-truth/20 text-truth h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <p className="text-gray-300 text-sm">Be skeptical of emotional or sensational content that seems designed to provoke strong reactions.</p>
                </li>
                <li className="flex gap-3 bg-black/20 border border-gray-800 rounded-lg p-4">
                  <div className="bg-truth/20 text-truth h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <p className="text-gray-300 text-sm">Check for inconsistencies in visual content - lighting, shadows, backgrounds, and facial features.</p>
                </li>
                <li className="flex gap-3 bg-black/20 border border-gray-800 rounded-lg p-4">
                  <div className="bg-truth/20 text-truth h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <p className="text-gray-300 text-sm">Pay attention to the language used - absolute claims, exaggerated statements, and emotional appeals often signal misinformation.</p>
                </li>
                <li className="flex gap-3 bg-black/20 border border-gray-800 rounded-lg p-4">
                  <div className="bg-truth/20 text-truth h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                    5
                  </div>
                  <p className="text-gray-300 text-sm">Look for citations and primary sources, especially for claims about health, science, or statistics.</p>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </ResourceModal>
  );
};

export default MisinformationQuiz;
